'use client';

import { JSX, useCallback } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import StepIndicator from '../StepIndicator';
import BillingForm from './BillingForm';
import { useCheckoutContext } from './CheckoutContext';
import PlanPricePreview from './PlanPricePreview';
import RecurlyCheckoutWrapper from './RecurlyCheckoutWrapper';
import { PurchaseProps, handleSubscriptionPurchase, redirectToSuccess } from './actions';

const logger = LoggerFactory.create('CheckoutClient');

export default function CheckoutClient({
    publicKey,
    showDiscountBadge = false,
}: {
    publicKey: string;
    showDiscountBadge?: boolean;
}): JSX.Element {
    const { currency, promoCode } = useCheckoutContext();

    const handlePurchaseSubmit = useCallback(
        (
            purchaseProps: PurchaseProps,
            setErrorMessage: (message: string) => void,
            resetSubmissionState: () => void,
        ) => {
            // Context automatically returns undefined when feature flag is disabled
            purchaseProps.promoCode = promoCode ?? undefined;

            const purchaseData = {
                ...purchaseProps,
                currency,
            };

            handleSubscriptionPurchase(purchaseData)
                .then((result) => {
                    logger.info({ result }, 'Purchase result');

                    if (typeof result === 'string') {
                        logger.error({ result }, 'Purchase failed with message');
                        setErrorMessage(result);
                        resetSubmissionState();
                    } else {
                        logger.info('Purchase successful, redirecting to success');
                        const aiToolReturnPath = sessionStorage.getItem('aiToolReturnPath');
                        sessionStorage.removeItem('aiToolReturnPath');
                        void redirectToSuccess(aiToolReturnPath ?? undefined);
                    }
                })
                .catch((error) => {
                    logger.error({ error: error }, 'Purchase failed');
                    setErrorMessage('An error occurred while processing your purchase. Please try again.');
                    resetSubmissionState();
                });
        },
        [promoCode, currency],
    );

    return (
        <div className="min-h-screen flex flex-col">
            {/* Step Indicator - Shows on mobile only, positioned after header */}
            <div className="lg:hidden px-6 py-4 bg-white">
                <StepIndicator stepNumber={2} showLogo={false} />
            </div>
            <div className="flex-1 flex flex-col lg:flex-row">
                {/* Left Panel - Checkout Form (shows second on mobile, first on desktop) */}
                <div className="w-full lg:w-2/3 bg-white order-2 lg:order-1">
                    <div className="px-6 py-8 lg:px-12">
                        <div className="max-w-2xl mx-auto">
                            {/* Step Indicator - Shows on desktop only */}
                            <div className="hidden lg:block">
                                <StepIndicator stepNumber={2} showLogo={false} />
                            </div>
                            <div className="mt-8">
                                <RecurlyCheckoutWrapper publicKey={publicKey}>
                                    <BillingForm onPurchaseSubmit={handlePurchaseSubmit} currency={currency} />
                                </RecurlyCheckoutWrapper>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Right Panel - Plan Selection (shows first on mobile, second on desktop) */}
                <div className="w-full lg:w-1/3 bg-gray-100 order-1 lg:order-2">
                    <div className="px-6 py-8 lg:px-8 lg:sticky lg:top-0">
                        <PlanPricePreview showDiscountBadge={showDiscountBadge} />
                    </div>
                </div>
            </div>
        </div>
    );
}
